import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ArrowRight, Award, Users, Star, ChevronRight, Check, Globe, MessageCircle } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HomePage = () => {
  const { language, isRTL } = useLanguage();
  const [gallery, setGallery] = useState([]);

  // User's photos
  const heroImage = "https://customer-assets.emergentagent.com/job_012d397a-361d-4aef-aae8-aa890f090aae/artifacts/1fj8rhxs_WhatsApp%20Image%202026-03-10%20at%2010.17.58%20PM.jpeg";
  const aboutImage = "https://customer-assets.emergentagent.com/job_012d397a-361d-4aef-aae8-aa890f090aae/artifacts/2ph3tizb_WhatsApp%20Image%202026-03-11%20at%204.48.43%20AM.jpeg";
  const logoIcon = "https://customer-assets.emergentagent.com/job_nina-beauty-academy/artifacts/79ucravx_WhatsApp_Image_2026-03-09_at_11.56.55_PM__1_-removebg-preview.png";

  // Helper to get full image URL
  const getImageUrl = (url) => {
    if (url.startsWith('/api/')) {
      return `${process.env.REACT_APP_BACKEND_URL}${url}`;
    }
    return url;
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const galleryRes = await axios.get(`${API}/gallery`);
        setGallery(galleryRes.data.slice(0, 6));
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);

  const whyChooseNina = [
    { en: "International Beauty Educator", ar: "مدربة تجميل دولية" },
    { en: "8+ years professional experience", ar: "أكثر من 8 سنوات خبرة احترافية" },
    { en: "Over 1000 trained students", ar: "أكثر من 1000 طالبة مدربة" },
    { en: "Small groups for personal attention", ar: "مجموعات صغيرة للاهتمام الشخصي" },
    { en: "Hands-on practical training", ar: "تدريب عملي تطبيقي" },
    { en: "International accredited certificate", ar: "شهادة دولية معتمدة" },
    { en: "Modern salon techniques", ar: "تقنيات صالون حديثة" },
  ];

  const languages = [
    { name: "English", flag: "🇬🇧" },
    { name: "Arabic", flag: "🇸🇦" },
    { name: "Russian", flag: "🇷🇺" },
    { name: "Romanian", flag: "🇷🇴" },
  ];

  const coursesPreview = [
    {
      title_en: "Russian Manicure Course",
      title_ar: "دورة المانيكير الروسي",
      description_en: "Learn the precision Russian manicure technique that creates perfectly clean cuticles and flawless nail preparation.",
      description_ar: "تعلمي تقنية المانيكير الروسي الدقيقة التي تخلق جلد ميت نظيف تماماً وتحضير أظافر لا تشوبه شائبة.",
      features_en: ["Professional e-file techniques", "Cuticle perfection", "Product application", "Salon speed techniques"],
      features_ar: ["تقنيات الجهاز الاحترافية", "إتقان الجلد الميت", "تطبيق المنتجات", "تقنيات سرعة الصالون"],
    },
    {
      title_en: "Nail Extensions Course",
      title_ar: "دورة تركيب الأظافر",
      description_en: "Learn modern gel nail extensions using professional shaping and apex techniques.",
      description_ar: "تعلمي تركيب الأظافر الجل الحديثة باستخدام تقنيات التشكيل الاحترافية.",
      features_en: ["Nail structure and architecture", "Upper forms technique", "Perfect apex creation", "Long-lasting results"],
      features_ar: ["هيكل وبنية الأظافر", "تقنية القوالب العلوية", "إنشاء apex مثالي", "نتائج طويلة الأمد"],
    },
    {
      title_en: "Lash Extension Master Course",
      title_ar: "دورة ماستر تركيب الرموش",
      description_en: "Master professional lash extensions and trending effects used in top beauty salons.",
      description_ar: "إتقان تركيب الرموش الاحترافية والتأثيرات الرائجة المستخدمة في أفضل صالونات التجميل.",
      features_en: ["Classic lash extensions", "Volume techniques", "Wispy and Kim K effect", "Perfect lash mapping"],
      features_ar: ["رموش كلاسيكية", "تقنيات الفوليوم", "تأثير ويسبي وكيم كي", "خريطة رموش مثالية"],
    },
  ];

  return (
    <div className="bg-[#FDFCF8]">
      {/* Hero Section */}
      <section
        data-testid="hero-section"
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
      >
        {/* Background Image */}
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('${heroImage}')`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#FDFCF8]/85 via-[#FDFCF8]/70 to-[#FDFCF8]" />

        {/* Content */}
        <div className="relative z-10 container mx-auto px-6 md:px-12 max-w-7xl text-center pt-32 pb-20">
          <div className="flex items-center justify-center gap-4 mb-6">
            <img 
              src={logoIcon} 
              alt="NC" 
              className="h-10 md:h-14 w-auto object-contain"
            />
            <h1 className={`${isRTL ? 'font-amiri' : 'font-playfair'} text-4xl md:text-6xl font-medium text-[#1A1A1A]`}>
              {language === 'en' ? 'Nina Beauty Academy' : 'أكاديمية نينا للتجميل'}
            </h1>
          </div>
          
          <p className={`${isRTL ? 'font-tajawal' : 'font-manrope'} text-lg md:text-xl text-[#C5A059] font-medium mb-4`}>
            {language === 'en' 
              ? 'International Beauty Educator & Top Master'
              : 'مدربة تجميل دولية وخبيرة معتمدة'}
          </p>
          
          <p className={`${isRTL ? 'font-tajawal' : 'font-manrope'} text-base md:text-lg text-[#666660] mb-10`}>
            {language === 'en' 
              ? 'Professional Nail & Lash Training in Riyadh'
              : 'تدريب احترافي على الأظافر والرموش في الرياض'}
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center flex-wrap">
            <Link to="/courses">
              <Button
                data-testid="hero-view-courses-btn"
                className="rounded-full bg-[#C5A059] text-white hover:bg-[#B08A3E] px-8 py-6 text-lg font-medium shadow-[0_4px_14px_0_rgba(197,160,89,0.39)] hover:shadow-[0_6px_20px_rgba(197,160,89,0.23)] hover:-translate-y-1 transition-all duration-300"
              >
                {language === 'en' ? 'View Courses' : 'عرض الدورات'}
                <ArrowRight className={`w-5 h-5 ${isRTL ? 'mr-2 rotate-180' : 'ml-2'}`} />
              </Button>
            </Link>
            <Link to="/contact">
              <Button
                data-testid="hero-book-training-btn"
                variant="outline"
                className="rounded-full border-2 border-[#C5A059] text-[#C5A059] hover:bg-[#C5A059] hover:text-white px-8 py-6 text-lg font-medium transition-all duration-300"
              >
                {language === 'en' ? 'Book Training' : 'احجزي تدريبك'}
              </Button>
            </Link>
            <a href="https://wa.me/966599385282" target="_blank" rel="noopener noreferrer">
              <Button
                data-testid="hero-whatsapp-btn"
                variant="outline"
                className="rounded-full border-2 border-[#25D366] text-[#25D366] hover:bg-[#25D366] hover:text-white px-8 py-6 text-lg font-medium transition-all duration-300"
              >
                <MessageCircle className={`w-5 h-5 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                {language === 'en' ? 'WhatsApp Consultation' : 'استشارة واتساب'}
              </Button>
            </a>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 rounded-full border-2 border-[#C5A059]/50 flex items-start justify-center p-2">
            <div className="w-1.5 h-3 bg-[#C5A059] rounded-full animate-scroll" />
          </div>
        </div>
      </section>

      {/* About Section */}
      <section data-testid="about-section" className="py-20 md:py-32">
        <div className="container mx-auto px-6 md:px-12 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
            {/* Image */}
            <div className="relative">
              <div className="aspect-[4/5] overflow-hidden rounded-2xl">
                <img
                  src={aboutImage}
                  alt="Nina Ceban"
                  className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
                />
              </div>
              {/* Decorative Element */}
              <div className="absolute -bottom-6 -right-6 w-48 h-48 border-2 border-[#C5A059] rounded-2xl -z-10" />
            </div>

            {/* Content */}
            <div className={isRTL ? 'text-right' : 'text-left'}>
              <p className="font-manrope text-xs md:text-sm uppercase tracking-[0.2em] text-[#C5A059] mb-4">
                {language === 'en' ? 'International Beauty Educator' : 'مدربة تجميل دولية'}
              </p>
              <h2
                data-testid="about-title"
                className={`${isRTL ? 'font-amiri' : 'font-playfair'} text-4xl md:text-5xl font-medium text-[#1A1A1A] mb-6`}
              >
                {language === 'en' ? 'Meet Nina Ceban' : 'تعرفي على نينا سيبان'}
              </h2>
              
              <div className={`${isRTL ? 'font-tajawal' : 'font-manrope'} text-base text-[#666660] leading-relaxed space-y-4`}>
                <p>
                  {language === 'en'
                    ? 'Nina Ceban is an International Beauty Educator, Top Master, and professional trainer based in Riyadh, specializing in advanced nail and lash techniques.'
                    : 'نينا سيبان هي مدربة تجميل دولية وخبيرة معتمدة ومدربة محترفة مقرها الرياض، متخصصة في تقنيات الأظافر والرموش المتقدمة.'}
                </p>
                <p>
                  {language === 'en'
                    ? 'With over 8 years of professional experience and more than 1000 trained students, Nina has built a reputation for delivering high-level education and helping beauty professionals grow their careers with confidence.'
                    : 'مع أكثر من 8 سنوات من الخبرة المهنية وأكثر من 1000 طالبة مدربة، بنت نينا سمعة قوية في تقديم تعليم عالي المستوى ومساعدة محترفات التجميل على تنمية حياتهن المهنية بثقة.'}
                </p>
                
                <p className="font-medium text-[#1A1A1A]">
                  {language === 'en' ? 'Her expertise includes:' : 'خبرتها تشمل:'}
                </p>
                <ul className="list-disc list-inside space-y-1 text-[#666660]">
                  <li>{language === 'en' ? 'Russian Manicure' : 'المانيكير الروسي'}</li>
                  <li>{language === 'en' ? 'Gel Nail Extensions' : 'تركيب الأظافر الجل'}</li>
                  <li>{language === 'en' ? 'Professional Lash Extensions' : 'تركيب الرموش الاحترافي'}</li>
                  <li>{language === 'en' ? 'Modern salon techniques used by top beauty masters' : 'تقنيات الصالون الحديثة المستخدمة من قبل أفضل خبراء التجميل'}</li>
                </ul>

                <p>
                  {language === 'en'
                    ? "Nina's courses are designed not only to teach technique, but to help students build a successful career in the beauty industry."
                    : 'دورات نينا مصممة ليس فقط لتعليم التقنيات، بل لمساعدة الطالبات على بناء مسيرة مهنية ناجحة في صناعة التجميل.'}
                </p>

                <p>
                  {language === 'en'
                    ? 'Nina Ceban is also invited as a judge at international beauty championships, where she evaluates the work of top nail and lash artists from different countries.'
                    : 'نينا سيبان مدعوة أيضاً كحكم في بطولات التجميل الدولية، حيث تقيم أعمال أفضل فنانات الأظافر والرموش من مختلف البلدان.'}
                </p>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-4 mt-8">
                <div className="text-center p-4 bg-[#F3ECE4] rounded-xl">
                  <Award className="w-8 h-8 text-[#C5A059] mx-auto mb-2" />
                  <p className="font-playfair text-2xl font-medium text-[#1A1A1A]">8+</p>
                  <p className="font-manrope text-xs text-[#666660]">
                    {language === 'en' ? 'Years Experience' : 'سنوات خبرة'}
                  </p>
                </div>
                <div className="text-center p-4 bg-[#F3ECE4] rounded-xl">
                  <Users className="w-8 h-8 text-[#C5A059] mx-auto mb-2" />
                  <p className="font-playfair text-2xl font-medium text-[#1A1A1A]">1000+</p>
                  <p className="font-manrope text-xs text-[#666660]">
                    {language === 'en' ? 'Students Trained' : 'طالبة مدربة'}
                  </p>
                </div>
                <div className="text-center p-4 bg-[#F3ECE4] rounded-xl">
                  <Star className="w-8 h-8 text-[#C5A059] mx-auto mb-2" />
                  <p className="font-playfair text-2xl font-medium text-[#1A1A1A]">5.0</p>
                  <p className="font-manrope text-xs text-[#666660]">
                    {language === 'en' ? 'Rating' : 'تقييم'}
                  </p>
                </div>
              </div>
              
              <p className={`${isRTL ? 'font-tajawal' : 'font-manrope'} text-sm text-[#666660] mt-4 italic`}>
                {language === 'en' 
                  ? "Students from different countries trust Nina's professional training and techniques."
                  : 'طالبات من مختلف البلدان يثقن بتدريب نينا الاحترافي وتقنياتها.'}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Courses Preview Section */}
      <section data-testid="courses-preview" className="py-20 md:py-32 bg-[#F3ECE4]/30">
        <div className="container mx-auto px-6 md:px-12 max-w-7xl">
          <div className={`${isRTL ? 'text-right' : 'text-left'} mb-16`}>
            <p className="font-manrope text-xs md:text-sm uppercase tracking-[0.2em] text-[#C5A059] mb-4">
              {language === 'en' ? 'Professional Training' : 'تدريب احترافي'}
            </p>
            <h2 className={`${isRTL ? 'font-amiri' : 'font-playfair'} text-4xl md:text-5xl font-medium text-[#1A1A1A] mb-4`}>
              {language === 'en' ? 'Our Professional Courses' : 'دوراتنا الاحترافية'}
            </h2>
            <p className={`${isRTL ? 'font-tajawal' : 'font-manrope'} text-lg text-[#666660] max-w-2xl`}>
              {language === 'en' 
                ? 'Master the most demanded beauty techniques and start your journey as a certified beauty professional.'
                : 'أتقني أكثر تقنيات التجميل طلباً وابدأي رحلتك كمحترفة تجميل معتمدة.'}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {coursesPreview.map((course, index) => (
              <Card
                key={index}
                data-testid={`course-preview-${index}`}
                className="bg-white border border-[#E6E2D6]/50 hover:border-[#C5A059]/30 transition-all duration-500 hover:shadow-xl rounded-2xl overflow-hidden"
              >
                <CardContent className="p-8">
                  <h3 className={`${isRTL ? 'font-amiri' : 'font-playfair'} text-xl font-medium text-[#1A1A1A] mb-4`}>
                    {language === 'en' ? course.title_en : course.title_ar}
                  </h3>
                  <p className={`${isRTL ? 'font-tajawal' : 'font-manrope'} text-[#666660] mb-6`}>
                    {language === 'en' ? course.description_en : course.description_ar}
                  </p>
                  <ul className="space-y-2">
                    {(language === 'en' ? course.features_en : course.features_ar).map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-[#666660]">
                        <Check className="w-4 h-4 text-[#C5A059] flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/courses">
              <Button className="rounded-full bg-[#C5A059] text-white hover:bg-[#B08A3E] px-8 py-6 text-lg font-medium">
                {language === 'en' ? 'View All Courses' : 'عرض جميع الدورات'}
                <ArrowRight className={`w-5 h-5 ${isRTL ? 'mr-2 rotate-180' : 'ml-2'}`} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Nina Section */}
      <section data-testid="why-choose-section" className="py-20 md:py-32">
        <div className="container mx-auto px-6 md:px-12 max-w-7xl">
          <div className={`${isRTL ? 'text-right' : 'text-left'} mb-16`}>
            <p className="font-manrope text-xs md:text-sm uppercase tracking-[0.2em] text-[#C5A059] mb-4">
              {language === 'en' ? 'Why Us' : 'لماذا نحن'}
            </p>
            <h2 className={`${isRTL ? 'font-amiri' : 'font-playfair'} text-4xl md:text-5xl font-medium text-[#1A1A1A]`}>
              {language === 'en' ? 'Why Students Choose Nina Ceban' : 'لماذا تختار الطالبات نينا سيبان'}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {whyChooseNina.map((item, index) => (
              <div
                key={index}
                className="flex items-center gap-4 p-6 bg-[#F3ECE4]/50 rounded-xl hover:bg-[#F3ECE4] transition-colors duration-300"
              >
                <div className="w-10 h-10 bg-[#C5A059] rounded-full flex items-center justify-center flex-shrink-0">
                  <Check className="w-5 h-5 text-white" />
                </div>
                <p className={`${isRTL ? 'font-tajawal' : 'font-manrope'} text-[#1A1A1A] font-medium`}>
                  {language === 'en' ? item.en : item.ar}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Preview */}
      <section data-testid="gallery-preview" className="py-20 md:py-32 bg-[#F3ECE4]/30">
        <div className="container mx-auto px-6 md:px-12 max-w-7xl">
          <div className={`${isRTL ? 'text-right' : 'text-left'} mb-16`}>
            <p className="font-manrope text-xs md:text-sm uppercase tracking-[0.2em] text-[#C5A059] mb-4">
              {language === 'en' ? 'See Our Work and Training Sessions' : 'شاهدي أعمالنا وجلسات التدريب'}
            </p>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
              <div>
                <h2 className={`${isRTL ? 'font-amiri' : 'font-playfair'} text-4xl md:text-5xl font-medium text-[#1A1A1A] mb-4`}>
                  {language === 'en' ? 'Our Gallery' : 'معرضنا'}
                </h2>
                <p className={`${isRTL ? 'font-tajawal' : 'font-manrope'} text-lg text-[#666660] max-w-2xl`}>
                  {language === 'en' 
                    ? 'Discover moments from our professional trainings, student practice sessions, and beauty work created with precision techniques.'
                    : 'اكتشفي لحظات من تدريباتنا الاحترافية وجلسات ممارسة الطالبات وأعمال التجميل المصنوعة بتقنيات دقيقة.'}
                </p>
              </div>
              <Link
                to="/gallery"
                className="group flex items-center gap-2 font-manrope text-[#C5A059] hover:text-[#B08A3E] transition-colors duration-300"
              >
                {language === 'en' ? 'View Gallery' : 'عرض المعرض'}
                <ChevronRight className={`w-4 h-4 group-hover:translate-x-1 transition-transform duration-300 ${isRTL ? 'rotate-180' : ''}`} />
              </Link>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {gallery.map((image, index) => (
              <div
                key={image.id}
                data-testid={`gallery-image-${index}`}
                className={`overflow-hidden rounded-xl ${
                  index === 0 ? 'md:col-span-2 md:row-span-2' : ''
                }`}
              >
                <img
                  src={getImageUrl(image.url)}
                  alt={language === 'en' ? image.caption_en : image.caption_ar}
                  className="w-full h-full object-cover aspect-square transition-transform duration-700 hover:scale-105"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Training Languages Section */}
      <section data-testid="languages-section" className="py-20 md:py-32">
        <div className="container mx-auto px-6 md:px-12 max-w-7xl">
          <div className={`${isRTL ? 'text-right' : 'text-left'} mb-12`}>
            <p className="font-manrope text-xs md:text-sm uppercase tracking-[0.2em] text-[#C5A059] mb-4">
              {language === 'en' ? 'Multilingual Training' : 'تدريب متعدد اللغات'}
            </p>
            <h2 className={`${isRTL ? 'font-amiri' : 'font-playfair'} text-4xl md:text-5xl font-medium text-[#1A1A1A]`}>
              {language === 'en' ? 'Training Languages' : 'لغات التدريب'}
            </h2>
          </div>

          <div className="flex flex-wrap justify-center gap-6">
            {languages.map((lang, index) => (
              <div
                key={index}
                className="flex items-center gap-3 px-8 py-4 bg-[#F3ECE4] rounded-full"
              >
                <span className="text-2xl">{lang.flag}</span>
                <span className="font-manrope text-lg font-medium text-[#1A1A1A]">{lang.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Brand Statement */}
      <section className="py-16 bg-[#1A1A1A]">
        <div className="container mx-auto px-6 md:px-12 max-w-5xl text-center">
          <p className={`${isRTL ? 'font-amiri' : 'font-playfair'} text-2xl md:text-3xl text-white font-medium italic`}>
            {language === 'en' 
              ? '"Transform your passion for beauty into a successful career with professional training from an international educator."'
              : '"حولي شغفك بالجمال إلى مسيرة مهنية ناجحة مع تدريب احترافي من مدربة دولية."'}
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section data-testid="cta-section" className="py-20 md:py-32">
        <div className="container mx-auto px-6 md:px-12 max-w-7xl">
          <div className="relative overflow-hidden bg-gradient-to-br from-[#C5A059] to-[#B08A3E] rounded-3xl p-12 md:p-20 text-center">
            {/* Decorative Elements */}
            <div className="absolute top-0 left-0 w-64 h-64 bg-white/10 rounded-full blur-3xl" />
            <div className="absolute bottom-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl" />

            <div className="relative z-10">
              <h2 className={`${isRTL ? 'font-amiri' : 'font-playfair'} text-4xl md:text-5xl font-medium text-white mb-6`}>
                {language === 'en' ? 'Ready to Start Your Journey?' : 'مستعدة لبدء رحلتك؟'}
              </h2>
              <p className={`${isRTL ? 'font-tajawal' : 'font-manrope'} text-lg text-white/90 mb-10 max-w-2xl mx-auto`}>
                {language === 'en' 
                  ? 'Join Nina Beauty Academy and become a certified beauty professional with internationally recognized techniques.'
                  : 'انضمي إلى أكاديمية نينا للتجميل وكوني محترفة تجميل معتمدة بتقنيات معترف بها دولياً.'}
              </p>
              <Link to="/contact">
                <Button
                  data-testid="cta-button"
                  className="rounded-full bg-white text-[#C5A059] hover:bg-[#1A1A1A] hover:text-white px-10 py-6 text-lg font-medium shadow-lg hover:-translate-y-1 transition-all duration-300"
                >
                  {language === 'en' ? 'Book Training Now' : 'احجزي تدريبك الآن'}
                  <ArrowRight className={`w-5 h-5 ${isRTL ? 'mr-2 rotate-180' : 'ml-2'}`} />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
